'use client';

import React, { useState, useEffect, useRef, useReducer } from 'react';

/**
 * Public interactive sandbox — session-scoped, no signup, no real data persisted.
 * All state lives in React state only. Nothing is written to Supabase.
 * Fictional contacts are used throughout. "Simulated" label is always visible.
 */

type SandboxStage = 'idle' | 's2_countdown' | 's3_notified' | 's4_escalated' | 'cancelled' | 'resolved';

interface SandboxState {
  stage: SandboxStage;
  countdown: number;
  claimedBy: string | null;
}

type SandboxAction =
  | { type: 'START' }
  | { type: 'TICK' }
  | { type: 'CANCEL' }
  | { type: 'ESCALATE_S3' }
  | { type: 'ESCALATE_S4' }
  | { type: 'CLAIM'; name: string }
  | { type: 'RESOLVE' }
  | { type: 'RESET' };

function sandboxReducer(state: SandboxState, action: SandboxAction): SandboxState {
  switch (action.type) {
    case 'START':      return { stage: 's2_countdown', countdown: 15, claimedBy: null };
    case 'TICK':       return { ...state, countdown: Math.max(0, state.countdown - 1) };
    case 'CANCEL':     return { ...state, stage: 'cancelled' };
    case 'ESCALATE_S3': return { ...state, stage: 's3_notified', countdown: 0 };
    case 'ESCALATE_S4': return { ...state, stage: 's4_escalated' };
    case 'CLAIM':      return { ...state, claimedBy: action.name };
    case 'RESOLVE':    return { ...state, stage: 'resolved' };
    case 'RESET':      return { stage: 'idle', countdown: 15, claimedBy: null };
    default:           return state;
  }
}

const FICTIONAL_CONTACTS = [
  { name: 'Sarah Thompson', relation: 'Daughter', tier: 1 },
  { name: 'James Thompson', relation: 'Son', tier: 1 },
  { name: 'Dr. Emily Carter', relation: 'GP', tier: 2 },
];

const STAGE_COLORS: Record<SandboxStage, string> = {
  idle:          'border-slate-700 bg-slate-900/40',
  s2_countdown:  'border-orange-600/60 bg-orange-950/20',
  s3_notified:   'border-red-600/60 bg-red-950/20',
  s4_escalated:  'border-red-500/80 bg-red-950/30',
  cancelled:     'border-green-700/40 bg-green-950/10',
  resolved:      'border-green-700/40 bg-green-950/10',
};

export default function PublicSandbox() {
  const [state, dispatch] = useReducer(sandboxReducer, { stage: 'idle', countdown: 15, claimedBy: null });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [autoEscalated, setAutoEscalated] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (state.stage !== 's2_countdown') {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      dispatch({ type: 'TICK' });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [state.stage]);

  // Auto-escalate when countdown hits 0
  useEffect(() => {
    if (state.stage === 's2_countdown' && state.countdown === 0 && !autoEscalated) {
      setAutoEscalated(true);
      if (timerRef.current) clearInterval(timerRef.current);
      dispatch({ type: 'ESCALATE_S3' });
    }
  }, [state.stage, state.countdown, autoEscalated]);

  const handleReset = () => {
    setAutoEscalated(false);
    dispatch({ type: 'RESET' });
  };

  return (
    <div className="rounded-2xl border border-[var(--rule)] overflow-hidden">
      {/* Sandbox header — "Simulated" label always visible */}
      <div className="flex items-center justify-between px-5 py-3 bg-amber-950/40 border-b border-amber-700/40">
        <div className="flex items-center gap-2">
          <span className="text-amber-400 text-sm" aria-hidden="true">⚠️</span>
          <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">Simulated</span>
          <span className="text-xs text-amber-600">— no real data, no real notifications, no signup required</span>
        </div>
        {state.stage !== 'idle' && (
          <button
            onClick={handleReset}
            className="text-xs text-amber-600 hover:text-amber-400 transition-colors"
          >
            Reset ↺
          </button>
        )}
      </div>

      <div className={`p-6 border-b border-[var(--rule)] transition-colors ${STAGE_COLORS[state.stage]}`}>
        {/* ── Idle ── */}
        {state.stage === 'idle' && (
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-[var(--ink)] font-semibold">Try it: simulate a fall detection</p>
              <p className="text-sm text-[var(--ink-2)]">
                Click the button below to trigger a simulated fall. You'll see the S2 countdown, the circle notification, and the claim protocol — exactly as a real user would experience it.
              </p>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-xl border border-[var(--rule)] bg-[var(--surface)]">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
                <span className="text-lg" aria-hidden="true">👤</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--ink)]">Margaret Thompson</p>
                <p className="text-xs text-[var(--ink-3)]">Example wearer · Springfield, IL</p>
              </div>
              <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-900/30 border border-green-700/40">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" aria-hidden="true" />
                <span className="text-xs text-green-400 font-medium">S0 — Monitoring</span>
              </div>
            </div>
            <button
              onClick={() => dispatch({ type: 'START' })}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-red-700 hover:bg-red-600 text-white font-semibold text-sm transition-colors"
            >
              <span aria-hidden="true">💥</span>
              Trigger simulated fall
            </button>
          </div>
        )}

        {/* ── S2 Countdown ── */}
        {state.stage === 's2_countdown' && (
          <div className="space-y-5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-widest text-orange-400 px-2 py-0.5 rounded bg-orange-900/40 border border-orange-700/40">S2 — Unconfirmed incident</span>
              <span className="text-xs text-[var(--ink-3)]">Simulated</span>
            </div>
            <div className="text-center space-y-2">
              <p className="text-sm text-[var(--ink-2)]">Guardian sent a check-in prompt to Margaret's watch.</p>
              <div className="text-6xl font-mono font-bold text-orange-400">{state.countdown}</div>
              <p className="text-xs text-[var(--ink-3)]">seconds until circle is notified</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => dispatch({ type: 'CANCEL' })}
                className="py-3 rounded-xl bg-green-700 hover:bg-green-600 text-white font-bold text-sm transition-colors"
              >
                I'm OK — cancel
              </button>
              <button
                onClick={() => { if (timerRef.current) clearInterval(timerRef.current); dispatch({ type: 'ESCALATE_S3' }); }}
                className="py-3 rounded-xl border border-orange-600/60 text-orange-400 hover:bg-orange-900/20 text-sm font-medium transition-colors"
              >
                Let it escalate →
              </button>
            </div>
          </div>
        )}

        {/* ── S3 Circle notified ── */}
        {state.stage === 's3_notified' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-widest text-red-400 px-2 py-0.5 rounded bg-red-900/40 border border-red-700/40">S3 — Circle notified</span>
              <span className="text-xs text-[var(--ink-3)]">Simulated</span>
            </div>
            <p className="text-sm text-[var(--ink-2)]">
              No response received. Guardian has notified Margaret's circle. Each member received a link to the responder view with her location and medical summary.
            </p>
            {/* Circle notification cards */}
            <div className="space-y-2">
              {FICTIONAL_CONTACTS.map((c, i) => (
                <div key={c.name} className="flex items-center justify-between p-3 rounded-xl border border-[var(--rule)] bg-[var(--surface)]">
                  <div>
                    <p className="text-sm font-medium text-[var(--ink)]">{c.name}</p>
                    <p className="text-xs text-[var(--ink-3)]">{c.relation} · Tier {c.tier}</p>
                  </div>
                  {state.claimedBy === c.name ? (
                    <span className="text-xs font-semibold text-green-400 px-2 py-1 rounded-full bg-green-900/30 border border-green-700/30">✓ Claimed</span>
                  ) : (
                    <button
                      onClick={() => dispatch({ type: 'CLAIM', name: c.name })}
                      className="text-xs px-3 py-1.5 rounded-lg border border-[var(--rule)] text-[var(--ink-2)] hover:text-[var(--ink)] hover:border-[var(--ink-3)] transition-colors"
                    >
                      {i === 0 ? 'I\'m going →' : 'Respond'}
                    </button>
                  )}
                </div>
              ))}
            </div>
            {state.claimedBy && (
              <div className="rounded-xl border border-green-700/40 bg-green-900/10 p-3">
                <p className="text-sm text-green-400 font-semibold">✓ {state.claimedBy} is responding</p>
                <p className="text-xs text-[var(--ink-3)] mt-0.5">Other circle members can see this. The incident is being handled.</p>
              </div>
            )}
            <div className="flex gap-3">
              {!state.claimedBy && (
                <button
                  onClick={() => dispatch({ type: 'ESCALATE_S4' })}
                  className="px-4 py-2.5 rounded-xl border border-red-600/60 text-red-400 hover:bg-red-900/20 text-sm font-medium transition-colors"
                >
                  No one responds → S4
                </button>
              )}
              {state.claimedBy && (
                <button
                  onClick={() => dispatch({ type: 'RESOLVE' })}
                  className="px-4 py-2.5 rounded-xl bg-green-700 hover:bg-green-600 text-white text-sm font-semibold transition-colors"
                >
                  Resolve — person is safe
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── S4 Escalated ── */}
        {state.stage === 's4_escalated' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-widest text-red-300 px-2 py-0.5 rounded bg-red-900/60 border border-red-600/60">S4 — Escalated</span>
              <span className="text-xs text-[var(--ink-3)]">Simulated</span>
            </div>
            <p className="text-sm text-[var(--ink-2)]">
              No circle member has responded. On the Professional plan, a staffed monitoring team is now reviewing this incident and attempting phone contact.
            </p>
            <div className="rounded-xl border border-red-700/40 bg-red-950/20 p-4 space-y-2">
              <p className="text-xs font-bold text-red-300 uppercase tracking-wide">If this were real:</p>
              <p className="text-sm text-[var(--ink-2)]">Call emergency services (999 / 911 / 112) immediately. Guardian cannot call them for you.</p>
            </div>
            <button
              onClick={() => dispatch({ type: 'RESOLVE' })}
              className="px-4 py-2.5 rounded-xl bg-green-700 hover:bg-green-600 text-white text-sm font-semibold transition-colors"
            >
              Resolve — situation handled
            </button>
          </div>
        )}

        {/* ── Cancelled ── */}
        {state.stage === 'cancelled' && (
          <div className="space-y-4 text-center">
            <div className="w-14 h-14 rounded-full bg-green-900/30 border border-green-700/40 flex items-center justify-center mx-auto">
              <span className="text-2xl" aria-hidden="true">✓</span>
            </div>
            <div>
              <p className="text-[var(--ink)] font-semibold">Alert cancelled</p>
              <p className="text-sm text-[var(--ink-2)] mt-1">
                Margaret tapped "I'm OK" before the countdown reached zero. The event was logged as a false alarm — no one was notified.
              </p>
            </div>
            <button onClick={handleReset} className="px-4 py-2.5 rounded-xl border border-[var(--rule)] text-sm text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors">
              Try again ↺
            </button>
          </div>
        )}

        {/* ── Resolved ── */}
        {state.stage === 'resolved' && (
          <div className="space-y-4 text-center">
            <div className="w-14 h-14 rounded-full bg-green-900/30 border border-green-700/40 flex items-center justify-center mx-auto">
              <span className="text-2xl" aria-hidden="true">✓</span>
            </div>
            <div>
              <p className="text-[var(--ink)] font-semibold">Incident resolved</p>
              <p className="text-sm text-[var(--ink-2)] mt-1">
                The incident has been closed. A full timestamped record — including who responded and when — is stored in the incident history.
              </p>
            </div>
            <button onClick={handleReset} className="px-4 py-2.5 rounded-xl border border-[var(--rule)] text-sm text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors">
              Try again ↺
            </button>
          </div>
        )}
      </div>

      {/* Stage ladder legend */}
      <div className="px-5 py-3 bg-[var(--surface)] flex flex-wrap gap-3">
        {[
          { code: 'S0', label: 'Monitoring', active: state.stage === 'idle' },
          { code: 'S2', label: 'Unconfirmed', active: state.stage === 's2_countdown' },
          { code: 'S3', label: 'Circle notified', active: state.stage === 's3_notified' },
          { code: 'S4', label: 'Escalated', active: state.stage === 's4_escalated' },
        ].map(s => (
          <div key={s.code} className={`flex items-center gap-1.5 text-xs ${s.active ? 'text-[var(--ink)]' : 'text-[var(--ink-3)]'}`}>
            <span className={`font-mono font-bold px-1.5 py-0.5 rounded text-white text-[10px] ${s.active ? 'bg-accent' : 'bg-slate-700'}`}>{s.code}</span>
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}
